import { Toast } from 'antd-mobile';
import { getToken } from './auth';
import i18n from '../i18n';

// 基础请求URL
const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'http://47.97.4.182:8080/api/v1';

// 响应基础类型
export interface BaseResponse<T = any> {
  success: boolean;
  data?: T;
  errorMessage?: string;
  errorCode?: string;
  showType?: number;
}

/**
 * 添加语言参数到 URL
 * @param url - 原始 URL
 * @param additionalParams - 额外的查询参数
 * @returns 添加了语言参数的完整 URL
 */
export function addLangParam(url: string, additionalParams?: Record<string, any>): string {
  // 获取当前语言，默认为 'zh'
  const currentLang = i18n.language || 'zh';
  // 转换为接口需要的格式
  const langParam = currentLang === 'en' ? 'en' : 'zh';
  
  // 合并所有参数
  const allParams = {
    ...additionalParams,
    lang: langParam,
  };
  
  // 构建查询字符串
  const queryParams = new URLSearchParams();
  Object.entries(allParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  
  const queryString = queryParams.toString();
  if (!queryString) {
    return url;
  }
  
  // 判断URL中是否已经包含查询参数
  if (url.includes('?')) {
    // 如果已经有 ?，使用 & 连接
    return `${url}&${queryString}`;
  } else {
    // 如果没有 ?，使用 ? 连接
    return `${url}?${queryString}`;
  }
}

// 请求函数
export async function request<T = any>(
  url: string, 
  options: RequestInit & { 
    data?: any; 
    params?: Record<string, any> 
  } = {}
): Promise<BaseResponse<T>> {
  const { data, params, ...rest } = options;
  
  // 获取当前语言，默认为 'zh'
  const currentLang = i18n.language || 'zh';
  // 转换为接口需要的格式（如果是 'zh' 就用 'zh'，如果是 'en' 就用 'en'）
  const langParam = currentLang === 'en' ? 'en' : 'zh';
  
  // 构建完整URL，添加查询参数
  let fullUrl = `${API_BASE_URL}${url}`;
  
  // 合并 params 和 lang 参数
  const allParams = {
    ...params,
    lang: langParam, // 自动添加 lang 参数
  };
  
  // 构建查询字符串
  const queryParams = new URLSearchParams();
  Object.entries(allParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      queryParams.append(key, String(value));
    }
  });
  
  const queryString = queryParams.toString();
  if (queryString) {
    // 判断URL中是否已经包含查询参数
    if (fullUrl.includes('?')) {
      // 如果已经有 ?，使用 & 连接
      fullUrl += `&${queryString}`;
    } else {
      // 如果没有 ?，使用 ? 连接
      fullUrl += `?${queryString}`;
    }
  }

  // 设置请求头，添加token等
  const headers = new Headers({
    'Content-Type': 'application/json',
    ...rest.headers,
  });

  const token = getToken();
  if (token) {
    headers.append('Authorization', `Bearer ${token}`);
  }

  try {
    const response = await fetch(fullUrl, {
      ...rest,
      headers,
      body: data ? JSON.stringify(data) : undefined,
    });
    console.log('响应拦截===》', response);

    // 获取响应数据
    let responseData: BaseResponse<T>;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      responseData = await response.json();
    } else {
      const text = await response.text();
      responseData = {
        success: response.ok,
        data: text as any,
      };
    }
    // 如果是401，跳转到登录页
    if (response.status === 401) {
      Toast.show({
        content: '请先登录',
        duration: 2000,
      });
      localStorage.removeItem('token');
      window.location.href = '/login';
      return {
        success: false,
        errorMessage: '请先登录',
        errorCode: 'UNAUTHORIZED',
      };
    }
    // 如果响应状态码非2xx，抛出错误
    if (!response.ok) {
      return {
        success: false,
        errorMessage: responseData.errorMessage || response.statusText,
        errorCode: responseData.errorCode || String(response.status),
      };
    }

    return responseData;
  } catch (error: any) {
    return {
      success: false,
      errorMessage: error.message || '请求失败',
      errorCode: 'NETWORK_ERROR',
    };
  }
}